import { createContext, useContext, useEffect, useState } from 'react';
import type { LiveCallQuery, LiveCallRecord } from './liveCallsState';
import type { RingCall } from './ringAnswer';

/** What LiveCallsProvider (liveCalls.tsx) hands out. Apart from the component so fast refresh keeps working. */
export interface LiveCalls {
  /** Inbound calls still ringing — the cards. */
  rings: RingCall[];
  /** The cards whose grace window for the phone's own invite has passed. */
  waited: string[];
  /** The agent's own "stop showing me this one"; the call keeps ringing. */
  dismiss: (key: string) => void;
  /** The record behind a call a phone here is holding, if it was heard. */
  find: (query: LiveCallQuery) => LiveCallRecord | null;
}

export const LiveCallsContext = createContext<LiveCalls | null>(null);

export function useLiveCalls(): LiveCalls {
  const value = useContext(LiveCallsContext);
  if (!value) throw new Error('useLiveCalls must be used within LiveCallsProvider');
  return value;
}

/**
 * The conversation behind a call a phone here is holding: what the phone's
 * holder said, else what this module heard the call ring as. The hint wins
 * because a hosted phone's holder — the dashboard — hears every ring across
 * pages, while this module is remounted on every visit.
 */
export function useCallConversation(query: LiveCallQuery & { hint?: number | null }): number | null {
  const { find } = useLiveCalls();
  if (typeof query.hint === 'number') return query.hint;
  return find(query)?.conversationDisplayId ?? null;
}

/**
 * Whole seconds since an epoch-ms stamp, ticking; 0 while the stamp is null.
 * The count is keyed to its stamp, so a new call never shows the old call's
 * last second while the first tick is still a beat away.
 */
export function useSecondsSince(since: number | null): number {
  const [tick, setTick] = useState<{ since: number; seconds: number } | null>(null);
  useEffect(() => {
    if (since === null) return;
    const run = () => setTick({ since, seconds: Math.max(0, Math.floor((Date.now() - since) / 1000)) });
    const timer = setInterval(run, 1000);
    return () => clearInterval(timer);
  }, [since]);
  return since !== null && tick?.since === since ? tick.seconds : 0;
}
